import type { EvalVariantOutput } from "contracts/api/evals/judge";
import { For, Show } from "solid-js";
import { PanelSection } from "../playground/PanelSection";

/**
 * The scored halves of one variant's output — signals, per-check judgments and
 * subcriterion assessments — mirroring `playground/RunScoreTables`. Each shows
 * only when it produced rows, so an empty section never reads as a zero score.
 */
export function EvalVariantScoreTables(props: { output: EvalVariantOutput }) {
  return (
    <>
      <Show when={props.output.signals.length > 0}>
        <PanelSection title="Signals" count={props.output.signals.length} flush>
          <table class="tbl">
            <thead>
              <tr>
                <th>Metric</th>
                <th>Score</th>
                <th>Detail</th>
              </tr>
            </thead>
            <tbody>
              <For each={props.output.signals}>
                {(signal) => (
                  <tr>
                    <td class="mono">{signal.metricKey}</td>
                    <td>{signal.score}</td>
                    <td>{signal.detail}</td>
                  </tr>
                )}
              </For>
            </tbody>
          </table>
        </PanelSection>
      </Show>

      <Show when={props.output.checkJudgments.length > 0}>
        <PanelSection
          title="Check judgments"
          count={props.output.checkJudgments.length}
          flush
        >
          <table class="tbl">
            <thead>
              <tr>
                <th>Metric</th>
                <th>Check</th>
                <th>Judgment</th>
                <th>Points</th>
                <th>Rationale</th>
              </tr>
            </thead>
            <tbody>
              <For each={props.output.checkJudgments}>
                {(judgment) => (
                  <tr>
                    <td class="mono">{judgment.metricKey}</td>
                    <td class="mono">{judgment.checkKey}</td>
                    <td>
                      <span class="badge">{judgment.judgment}</span>
                    </td>
                    <td>{judgment.points}</td>
                    <td>{judgment.rationale}</td>
                  </tr>
                )}
              </For>
            </tbody>
          </table>
        </PanelSection>
      </Show>

      <Show when={props.output.assessments.length > 0}>
        <PanelSection
          title="Assessments"
          count={props.output.assessments.length}
          flush
        >
          <table class="tbl">
            <thead>
              <tr>
                <th>Subcriterion</th>
                <th>Score</th>
                <th>State</th>
                <th>Rationale</th>
              </tr>
            </thead>
            <tbody>
              <For each={props.output.assessments}>
                {(assessment) => (
                  <tr>
                    <td class="mono">
                      {assessment.subcriterionKey}
                      <div class="landing-sub">
                        {assessment.parentMetricKey}
                      </div>
                    </td>
                    {/* Null is an explicit absence, never a stand-in zero. */}
                    <td>{assessment.score ?? "—"}</td>
                    <td>
                      <span class="badge">{assessment.verificationState}</span>
                    </td>
                    <td>{assessment.rationale}</td>
                  </tr>
                )}
              </For>
            </tbody>
          </table>
        </PanelSection>
      </Show>
    </>
  );
}
